import {
  Box,
  DateField,
  dayjs,
  MenuItem,
  NotificationCard,
  Select,
  Stack,
  TextField,
  Typography,
  useBirthDateValidator,
  useFirstNameValidator,
  useLastNameValidator,
  useSelectValidator,
  Link,
} from '@ace-clublabs/shared-component-library';
import { useState } from 'react';
import { useGlobalState } from '../../../state/state';
import { getGenderList, getLoggedInMember } from '../../../state/helpers';
import { MSSMembers, MSSNotificationType } from '@lib/types';

export const PersonalInfo = (props: { setFormHasChanged: () => void; hasError: boolean }) => {
  const { setFormHasChanged } = props;
  const [membershipDetails] = useGlobalState('memberDetails');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const urlParams = new URLSearchParams(window.location.search);
  const familyMemberCustomerId = urlParams.get('custId') || ''; //only family members will include custId query param
  const isFamilyMemberContact = familyMemberCustomerId ? true : false;

  const memberInfo = isFamilyMemberContact
    ? getFamilyMemberContact(familyMemberCustomerId, membershipDetails?.members)
    : getLoggedInMember(membershipDetails);

  const genderList = getGenderList(membershipDetails);
  const [formData, setFormData] = useState({
    firstName: memberInfo?.firstName || '',
    middleName: memberInfo?.middleName || '',
    lastName: memberInfo?.lastName || '',
    nameSuffix: memberInfo?.nameSuffix || '',
    gender: memberInfo?.gender || '',
    dateOfBirth: memberInfo?.dateOfBirth || '',
  });

  const suffixList = ['Jr.', 'Sr.', 'II', 'III', 'IV', 'DDS', 'PHD', 'ESQ'];

  const currentMemberRole = membershipDetails?.prospectiveMemberRules?.memberRoles?.find(
    (role) => role.roleType === memberInfo?.roleType
  );

  const firstNameValidator = useFirstNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });

  const middleNameValidator = useFirstNameValidator({
    maxLength: 1,
    validationFeedback: 'immediate',
    filterCharacterPattern: '[^a-z]',
    trim: false,
    required: false,
  });

  const lastNameValidator = useLastNameValidator({
    maxLength: 20,
    validationFeedback: 'immediate',
    filterCharacterPattern: "[^a-z' -]",
    trim: false,
    pattern: "^(?=.*[A-Za-z])([A-Za-z'-]*[\\s]?[A-Za-z'-]*)$",
  });

  const nameSuffixValidator = useSelectValidator();

  const birthdayValidator = useBirthDateValidator({
    minAge: currentMemberRole?.minimumAge,
    maxAge: currentMemberRole?.maximumAge,
    required: currentMemberRole?.isBirthDateRequired,
  });

  const genderValidator = useSelectValidator({ validationFeedback: 'immediate' });

  return (
    <div className="personal-info-section">
      <Typography variant="h5" sx={{ marginBottom: '16px' }} className="contact-details-title">
        Edit information
      </Typography>
      {props.hasError ? (
        <Stack sx={{ marginBottom: '2rem', maxWidth: '100vw' }}>
          <Stack
            alignItems="stretch"
            gap="0.5rem"
            flexDirection={isMobile ? 'row' : 'column'}
            sx={{
              overflowX: 'scroll',
              height: '100%',
              margin: '0 -1.5rem',
              padding: '0 1.5rem',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <NotificationCard
              severity={MSSNotificationType.ERROR}
              sx={{
                maxWidth: '100%',
                minWidth: isMobile ? '332px' : undefined,
                opacity: undefined,
                '& .MuiPaper-root': {
                  height: '100%',
                  alignItems: 'center',
                },
              }}
            >
              <Typography className={`${MSSNotificationType.ERROR.toLowerCase()}-notification`} variant="b2Regular">
                <Stack className="message-container" direction="row">
                  <Box>
                    Sorry, we couldn't update your information. Please try again or give us a call at{' '}
                    <Link variant="b2Regular" id={'secured-member-card-link'} href="tel:1-844-579-873" target="_blank">
                      1-844-579-873
                    </Link>
                    {'.'}
                  </Box>
                </Stack>
              </Typography>
            </NotificationCard>
          </Stack>
        </Stack>
      ) : (
        <></>
      )}
      <Stack direction={'column'} gap={'16px'}>
        <Stack direction={'row'} justifyContent={'space-between'} gap={'16px'}>
          <TextField
            name="firstName"
            aria-label="firstName"
            fullWidth
            value={formData.firstName}
            label="First name"
            id="firstName"
            InputProps={{ readOnly: memberInfo?.firstName ? true : false }}
            validator={firstNameValidator}
            onChange={(v) => {
              setFormData({ ...formData, firstName: v.currentTarget.value });
            }}
            onClearButtonClick={() => {
              setFormData({ ...formData, firstName: '' });
              setFormHasChanged();
            }}
            sx={{
              '& .Mui-disabled .MuiInputBase-input': {
                WebkitTextFillColor: '#707070 !important',
              },
            }}
            data-qm-block="true"
          />
          {memberInfo?.middleName ? (
            <TextField
              name="middleName"
              aria-label="middleInitial"
              value={formData.middleName.charAt(0)}
              label="M.I."
              id="middleInitial"
              validator={middleNameValidator}
              onChange={(v) => setFormData({ ...formData, middleName: v.currentTarget.value })}
              onClearButtonClick={() => {
                setFormData({ ...formData, middleName: '' });
                setFormHasChanged();
              }}
              sx={{
                maxWidth: '126px !important',
                '& input': {
                  textTransform: 'capitalize',
                },
              }}
              data-qm-block="true"
            />
          ) : (
            <></>
          )}
        </Stack>
        <Stack direction={'row'} gap={'16px'} justifyContent={'space-between'}>
          <TextField
            name="lastName"
            aria-label="lastName"
            fullWidth
            value={formData.lastName}
            label="Last name"
            id="lastName"
            required
            validator={lastNameValidator}
            onChange={(v) => {
              setFormData({ ...formData, lastName: v.currentTarget.value });
              setFormHasChanged();
            }}
            onClearButtonClick={() => {
              setFormData({ ...formData, lastName: '' });
              setFormHasChanged();
            }}
            data-qm-block="true"
          />
          {memberInfo?.nameSuffix ? (
            <div style={{ maxWidth: '126px !important' }}>
              <Select
                name="suffix"
                label="Suffix"
                id="suffixDropdown"
                helperText=""
                // validator={useSelectValidator()}
                validator={nameSuffixValidator}
                value={formData.nameSuffix}
                fullScreenThreshold={9}
                aria-label="suffix"
                data-qm-block="true"
                onChange={(v) => {
                  setFormData({ ...formData, nameSuffix: v.target.value });
                  setFormHasChanged();
                }}
              >
                {suffixList.map((suffix) => (
                  <MenuItem key={suffix} value={suffix} id={suffix} data-qm-block="true">
                    {suffix}
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : (
            <></>
          )}
        </Stack>
        <Stack direction={'row'} gap={'16px'} justifyContent={'space-between'}>
          <DateField
            aria-label="dateOfBirth"
            name="dateOfBirth"
            label="Date of birth"
            id="contactDOB"
            sx={{ maxWidth: '270px' }}
            fullWidth
            helperText={'mm/dd/yyyy'}
            onClearButtonClick={() => {
              setFormData({ ...formData, dateOfBirth: '' });
              setFormHasChanged();
            }}
            validator={birthdayValidator}
            value={formData.dateOfBirth === '' ? null : dayjs(formData.dateOfBirth)}
            onChange={(v) => setFormData({ ...formData, dateOfBirth: v?.format('MM/DD/YYYY') ?? '' })}
            data-qm-block="true"
          />
          <Select
            label="Gender"
            name="gender"
            id="gender"
            fullWidth
            helperText="Must match your driver's license or ID"
            value={genderList.find((g) => {
              return g.genderValue === (formData.gender ?? '');
            })}
            getDisplayText={(v: any) => {
              return v.genderName;
            }}
            readOnly={memberInfo?.gender ? true : false}
            validator={genderValidator}
            onChange={(v: any) => {
              const x = v.target.value;
              setFormData({ ...formData, gender: x.genderValue });
            }}
            sx={{
              '& .Mui-disabled': {
                WebkitTextFillColor: '#707070 !important',
              },
            }}
            data-qm-block="true"
          >
            {genderList.map((gender, i) => (
              <MenuItem key={gender.genderName} value={gender as any} id={gender.genderValue} data-qm-block="true">
                {gender.genderName}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
    </div>
  );
};

function getFamilyMemberContact(customerId: string, members: MSSMembers[] | undefined) {
  return members?.find((m) => m.customerId === customerId);
}
